/* eslint-disable @typescript-eslint/no-empty-function */
import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { UmssIconComponent } from '../icon/icon.component';

let nextUniqueId = 0;

@Component({
  selector: 'umss-currency-input',
  standalone: true,
  imports: [CommonModule, UmssIconComponent],
  templateUrl: './currency-input.component.html',
  styleUrls: ['./currency-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UmssCurrencyInputComponent),
      multi: true
    }
  ]
})
export class UmssCurrencyInputComponent implements ControlValueAccessor {
  @HostBinding('style.display') display = 'contents';

  @ViewChild('inputEl') inputEl!: ElementRef<HTMLInputElement>;

  @Input() size: 'Mini' | 'Small' | 'Regular' | 'Large' = 'Regular';
  @Input() disabled = false;
  @Input() required = false;
  @Input() error = false;
  @Input() placeholder = '0.00';
  @Input() label?: string;
  @Input() helperText?: string;
  @Input() errorMessage?: string;
  @Input() name?: string;
  /** Maximum allowed value in dollars */
  @Input() max = 99_999.99;
  @Input() formatOn: 'input' | 'blur' = 'input';
  @Input() suffix?: string;

  @Output() valueChange = new EventEmitter<number | null>();
  @Output() onBlur = new EventEmitter<void>();
  @Output() onFocus = new EventEmitter<void>();

  /** Internal state in cents to avoid floating-point accumulation errors */
  _centAmount = 0;
  editableValue = '0.00';

  private readonly _uid = `umss-currency-input-${nextUniqueId++}`;

  get inputId(): string {
    return this.name ?? this._uid;
  }

  get errorId(): string {
    return `${this._uid}-error`;
  }

  get helperId(): string {
    return `${this._uid}-helper`;
  }

  get suffixId(): string {
    return `${this._uid}-suffix`;
  }

  get describedBy(): string | null {
    const ids: string[] = [];
    if (this.error && this.errorMessage) ids.push(this.errorId);
    else if (!this.error && this.helperText) ids.push(this.helperId);
    if (this.suffix) ids.push(this.suffixId);
    return ids.length ? ids.join(' ') : null;
  }

  get ariaLabel(): string {
    return this.label ?? 'Amount in US dollars';
  }

  private _onChange: (value: number | null) => void = () => {};
  private _onTouched: () => void = () => {};

  get displayValue(): string {
    return (this._centAmount / 100).toFixed(2);
  }

  get iconSize(): string {
    switch (this.size) {
      case 'Mini':
        return '14px';
      case 'Small':
        return '16px';
      case 'Large':
        return '20px';
      default:
        return '18px';
    }
  }

  writeValue(value: number | null): void {
    this._centAmount = Math.round((value ?? 0) * 100);
    this.editableValue = this.displayValue;
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onKeyDown(event: KeyboardEvent): void {
    if (this.disabled) return;
    if (this.formatOn === 'blur') return;

    const allowPassthrough = [
      'Tab',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End'
    ];
    if (allowPassthrough.includes(event.key)) return;

    event.preventDefault();

    const input = event.target as HTMLInputElement;
    const selectionStart = input.selectionStart;
    const selectionEnd = input.selectionEnd;
    const hasSelection =
      selectionStart !== null &&
      selectionEnd !== null &&
      selectionStart !== selectionEnd;

    if (event.key >= '0' && event.key <= '9') {
      const newAmount = this._centAmount * 10 + parseInt(event.key, 10);
      if (newAmount / 100 <= this.max) {
        this._centAmount = newAmount;
        this._emit();
      }
    } else if (
      (event.key === 'Backspace' || event.key === 'Delete') &&
      hasSelection
    ) {
      const valueWithoutSelection =
        this.displayValue.slice(0, selectionStart ?? undefined) +
        this.displayValue.slice(selectionEnd ?? undefined);
      this._centAmount = Number(valueWithoutSelection.replace(/\D/g, '')) || 0;
      this._emit();
    } else if (event.key === 'Backspace') {
      this._centAmount = Math.floor(this._centAmount / 10);
      this._emit();
    } else if (event.key === 'Delete') {
      this._centAmount = 0;
      this._emit();
    }
  }

  onPaste(event: ClipboardEvent): void {
    if (this.disabled) return;
    if (this.formatOn === 'blur') return;
    event.preventDefault();

    const text = event.clipboardData?.getData('text') ?? '';
    const digits = text.replace(/\D/g, '');
    if (!digits) return;

    let amount = 0;
    for (const d of digits) {
      amount = amount * 10 + parseInt(d, 10);
      if (amount / 100 > this.max) {
        amount = Math.floor(amount / 10);
        break;
      }
    }
    this._centAmount = amount;
    this._emit();
  }

  onFocusEvent(): void {
    if (this.disabled) return;
    this.onFocus.emit();
  }

  onBlurEvent(): void {
    if (this.disabled) return;
    if (this.formatOn === 'blur') {
      this.editableValue = this.displayValue;
    }
    this._onTouched();
    this.onBlur.emit();
  }

  /** Resets any character the browser manages to insert before our keydown fires */
  onNativeInput(event: Event): void {
    const el = event.target as HTMLInputElement;

    if (this.formatOn === 'blur') {
      this.editableValue = el.value;
      const parsedValue = Number(this.editableValue);

      if (
        this.editableValue === '' ||
        (Number.isFinite(parsedValue) &&
          parsedValue >= 0 &&
          parsedValue <= this.max)
      ) {
        this._centAmount = Math.round((parsedValue || 0) * 100);
        this._emit();
      }
      return;
    }

    el.value = this.displayValue;
  }

  focusInput(): void {
    this.inputEl?.nativeElement.focus();
  }

  /** Keeps clicks on the decorative chrome from stealing focus from the input */
  onContainerMouseDown(event: MouseEvent): void {
    if (this.disabled || event.target === this.inputEl?.nativeElement) return;
    event.preventDefault();
    this.focusInput();
  }

  private _emit(): void {
    const val = this._centAmount / 100;
    this._onChange(val);
    this.valueChange.emit(val);
  }
}
